import { useState } from 'react';
import { CollapsibleSection, Progress } from '@indipia/ui-core';
import { useAppStore, useAuthStore } from '@indipia/shared';
import { PhotoLightbox } from './PhotoLightbox';
import { ThumbImg } from './ThumbImg';
import { ProcessChart, type ProcessStep } from './process-chart';
import type { BasicInfoWorklog } from '../types/worklog.types';

/**
 * CustomerReportView — 고객이 보게 될 리포트 본문(공개 뷰, 설계서 §5.6 "유지"). `CustomerReportSharePage`
 * (고객 공개 뷰, §4.1.13 스냅샷)의 유일한 소비처다 — 설정(CustomerReportSettingPage)·미리보기
 * (CustomerReportPreviewPage) 화면은 폐기됐다(§5.6).
 *
 * - 헤더/진척율/타임라인 노출은 공개 설정(settings)에 따라 분기.
 * - 타임라인(groups)은 발송 스냅샷(§4.1.13 reportCards)을 작업일별로 집계한 **실데이터**.
 */

/** 고객리포트 공개 설정 — 확정 명세 §4.2 필드 + 스케줄 공개(마스터, 진척율·공정현황을 함께 토글). */
export interface ReportSettings {
  /** 스케줄 공개(마스터, UI 전용) — off면 진척율·공정 현황을 모두 숨긴다. */
  showScheduleYn: boolean;
  /** 공사 진척율 표시 (§4.2 displayProgressYn) */
  displayProgressYn: boolean;
  /** 공정 현황(노선도) 표시 (§4.2 displayChartYn) */
  displayChartYn: boolean;
  /** 사진 배열(그리드 열 수) 2~6 (§4.2 photoColCnt) */
  photoColCnt: number;
}

export const DEFAULT_SETTINGS: ReportSettings = {
  showScheduleYn: true,
  displayProgressYn: true,
  displayChartYn: true,
  photoColCnt: 4,
};

// ── 고객공유 타임라인(실데이터) 모델 ───────────────────────────────
export interface ReportPhoto {
  url: string;
  thumbUrl: string;
}
export interface ReportGroup {
  /** 표시용 날짜 라벨 (예: '2026.06.11 (목)') */
  date: string;
  /** 정렬 키(YYYY-MM-DD) */
  dateKey: string;
  /** 그 날짜 보고들의 공유 문장 — 접수 순서대로 머지 */
  contents: string[];
  /** 그 날짜 보고들의 공유 사진 — 접수 순서대로 머지 */
  photos: ReportPhoto[];
}

const pad2 = (n: number) => String(n).padStart(2, '0');

export function fmtPeriod(start: string | null, end: string | null): string {
  if (!start || !end) return '-';
  const s = new Date(start);
  const e = new Date(end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return `${start} ~ ${end}`;
  return `${s.getFullYear()}.${pad2(s.getMonth() + 1)}.${pad2(s.getDate())} ~ ${pad2(e.getMonth() + 1)}.${pad2(e.getDate())}`;
}
export function totalDays(start: string | null, end: string | null): number | null {
  if (!start || !end) return null;
  const s = new Date(start);
  const e = new Date(end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return null;
  return Math.max(1, Math.round((e.getTime() - s.getTime()) / 86400000) + 1);
}

export function CustomerReportView({
  worklog,
  settings,
  groups,
  groupsLoading = false,
  constDays,
  progressRate,
  steps = null,
  stepsLoading = false,
  hasSchedule = true,
  companyName: companyNameProp,
}: {
  worklog: BasicInfoWorklog;
  settings: ReportSettings;
  groups: ReportGroup[];
  groupsLoading?: boolean;
  /** 총 공사일수(근무일 기준: 월~금, 토·일요일·공휴일 제외). 미지정 시 달력일수로 폴백. */
  constDays?: number | null;
  /** 공사 진척율(%) — 총 공사일수 대비 오늘까지 경과 공사일수의 일할 비율. */
  progressRate?: number | null;
  /** 공정 스텝(인디플래너 스케줄 기반). []=미연동, null=로딩. */
  steps?: ProcessStep[] | null;
  stepsLoading?: boolean;
  /** 스케줄 실재 연동 여부. false면 '공정 현황'을 렌더하지 않는다(스케줄 없는 현장). */
  hasSchedule?: boolean;
  /** 시공사 표기 — 고객 공개 뷰(§4.3)가 서버값을 넘긴다. 미지정 시 로그인 세션에서 얻는다. */
  companyName?: string | null;
}) {
  const [procOpen, setProcOpen] = useState(true);
  const [lb, setLb] = useState<{ photos: ReportPhoto[]; index: number } | null>(null);
  // 리포트 상단 시공사 표기 — 사장님(인테리어 업체)의 사업체명(compName).
  // 사장님 화면(설정·미리보기)은 세션에서 얻지만, 고객 공개 뷰는 로그인이 없어 세션이 비어 있다.
  // 그래서 공유 화면이 서버값(§4.3 companyName)을 prop 으로 넘긴다 — 없을 때만 세션 폴백.
  const sessionComp = useAppStore((s) => s.session?.compName);
  const authComp = useAuthStore((s) => s.user?.compName);
  const companyName = companyNameProp || sessionComp || authComp || '시공사';

  const showProgress = settings.showScheduleYn && settings.displayProgressYn;
  // 스케줄이 없는(삭제·미연동) 현장은 공정 현황(및 완공)을 표시하지 않는다 — 설정이 켜져 있어도 강제 숨김.
  const showChart = settings.showScheduleYn && settings.displayChartYn && hasSchedule;
  const showProgressSection = showProgress || showChart;
  // 총 공사일수 — 근무일(월~금, 토·일요일·공휴일 제외) 우선, 없으면 달력일수 폴백.
  const days = constDays ?? totalDays(worklog.constStartDate, worklog.constEndDate);

  return (
    <>
      {/* 고객 시점 리포트 헤더 */}
      <div className="mb-5 rounded-lg border border-border bg-surface-raised p-4">
        <div className="mb-2 flex items-center gap-2">
          <span className="flex h-6 w-6 items-center justify-center rounded-md bg-group text-[10px] font-bold text-group-onPrimary">IN</span>
          <span className="text-body-sm font-bold text-text-secondary">{companyName} · 시공 현황 리포트</span>
        </div>
        {/* 현장명 / 고객명 */}
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="text-heading-sm font-bold tracking-tight text-text">{worklog.siteName ?? '(현장명 없음)'}</span>
          <span className="text-border">/</span>
          <span className="text-[13px] font-semibold text-text-secondary">{worklog.customerName ?? '-'} 고객님</span>
        </div>
        {/* 현장주소  공사기간  총소요일수  D-day */}
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[length:var(--type-caption-size)] text-text-muted">
          {worklog.siteAddress && (
            <>
              <span className="min-w-0 truncate">{worklog.siteAddress}</span>
              <span className="text-border">·</span>
            </>
          )}
          <span>{fmtPeriod(worklog.constStartDate, worklog.constEndDate)}</span>
          {days != null && (
            <>
              <span className="text-border">·</span>
              <span>총 {days}일</span>
            </>
          )}
        </div>
      </div>

      {/* 공사 진척율 + 공정 현황(노선도) — 예시(스케줄 연동 전) */}
      {showProgressSection && (
        <div className="mb-7 rounded-lg border border-border bg-surface-raised p-4">
          {showProgress && (
            <div className={showChart ? 'mb-3.5' : ''}>
              <div className="mb-2 text-body-md font-bold tracking-tight text-text">
                공사 진척율 <span className="ml-1 text-[15px] font-bold text-group">{progressRate ?? 0}%</span>
              </div>
              {/*
                표준 Progress(D-09 §15.26). `.prog` 는 width:280px 고정이라 카드 폭을 채우도록
                `!w-full` 로만 덮는다(높이·색·radius 는 토큰 그대로 사용).
              */}
              <Progress
                value={progressRate ?? 0}
                className="!w-full"
                role="progressbar"
                aria-valuenow={progressRate ?? 0}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label="공사 진척율"
              />
            </div>
          )}

          {showChart && (
            /*
              표준 CollapsibleSection(D-09 §15.31) — 제어형이라 기존 procOpen 상태를 그대로 쓴다.
              수제 헤더(클릭 div + 회전 svg)를 걷어내면서 접근성(button·aria-expanded)·화살표 회전이
              표준 CSS로 넘어간다. 범례는 헤더 우측 슬롯(date)에 얹는다.
              로딩은 loading/skeleton 슬롯이 처리 — open 여부와 무관하게 노출되던 기존 동작과 달리
              '펼쳤을 때만' 보이게 하려고 loading 은 procOpen 과 함께 판단한다.
            */
            <CollapsibleSection
              title="공정 현황"
              open={procOpen}
              onToggle={() => setProcOpen((v) => !v)}
              date={
                steps && steps.length > 0 ? (
                  <span className="flex items-center gap-3">
                    <LegendItem color="var(--color-success)" label="완료" />
                    <LegendItem color="var(--color-info)" label="시공중" />
                    <LegendItem color="var(--shell-border)" label="시작전" />
                  </span>
                ) : undefined
              }
              loading={procOpen && (stepsLoading || steps === null)}
              skeleton={
                <div className="py-6 text-center text-body-sm text-text-muted">공정 정보를 불러오는 중…</div>
              }
            >
              {steps && steps.length === 0 ? (
                <div className="rounded-md border border-dashed border-border py-6 text-center text-body-sm text-text-muted">
                  인디플래너 스케줄이 연동되면 공정 현황이 표시됩니다.
                </div>
              ) : (
                steps && <ProcessChart steps={steps} />
              )}
            </CollapsibleSection>
          )}
        </div>
      )}

      {/* 타임라인 — 고객공유 선별 컨텐츠(실데이터, 최신순) */}
      {groupsLoading ? (
        <div className="py-12 text-center text-[13px] text-text-muted">고객공유 내용을 불러오는 중…</div>
      ) : groups.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border py-12 text-center">
          <p className="text-[13.5px] font-semibold text-text">아직 고객에게 공유된 내용이 없습니다.</p>
          <p className="mt-1 text-body-sm text-text-secondary">현장보고에서 문장·사진을 고객공유로 지정하면 이곳에 표시됩니다.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map((g) => (
            <div key={g.dateKey}>
              <div className="mb-3 flex items-center gap-2.5 text-heading-sm font-bold tracking-tight text-text">
                <span>{g.date}</span>
                <span className="h-px flex-1 bg-border" />
              </div>
              <div className="overflow-hidden rounded-lg border border-border bg-surface-raised p-4">
                {/* 같은 날짜 문장 — 접수 순서대로 머지 */}
                {g.contents.length > 0 && (
                  <p className="whitespace-pre-line rounded-r-md border-l-[3px] border-group/50 bg-surface-muted px-4 py-3 text-[13.5px] leading-relaxed text-text">
                    {g.contents.join('\n')}
                  </p>
                )}
                {/* 같은 날짜 사진 — 접수 순서대로 머지 */}
                {g.photos.length > 0 && (
                  <div
                    className="mt-3 grid gap-2"
                    style={{ gridTemplateColumns: `repeat(${settings.photoColCnt}, minmax(0, 1fr))` }}
                  >
                    {g.photos.map((p, pi) => (
                      <button
                        key={pi}
                        type="button"
                        onClick={() => setLb({ photos: g.photos, index: pi })}
                        className="aspect-[4/3] overflow-hidden rounded-md border border-border transition-transform hover:scale-[1.03]"
                        aria-label="사진 크게 보기"
                      >
                        <ThumbImg url={p.thumbUrl} className="h-full w-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <PhotoLightbox
        photos={(lb?.photos ?? []).map((p) => ({ url: p.url }))}
        index={lb?.index ?? null}
        onClose={() => setLb(null)}
        onIndexChange={(i) => setLb((prev) => (prev ? { ...prev, index: i } : prev))}
        renderImage={(photo, style) => <ThumbImg url={photo.url} alt="" imgStyle={style} />}
      />
    </>
  );
}

function LegendItem({ color, label }: { color: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5 text-caption text-text-muted">
      <span className="h-2.5 w-2.5 rounded-full" style={{ background: color }} />
      {label}
    </span>
  );
}
